import React, { useState, useCallback } from 'react';
import { Box, Typography, InputBase, IconButton, Button, Stack, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import OutlinedButton from '../../ui-library/OutlinedButton';
import {
  primitiveColors,
  semanticColors,
  semanticGradients,
  BORDER_RADIUS,
  BORDER_RADIUS_LG,
} from '@/theme/tokens';
import { getBrandGradientWithOpacity } from '@/theme/utils';
import {
  HandThumbUpIcon as HandThumbUpOutline,
  HandThumbDownIcon as HandThumbDownOutline,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolid,
  HandThumbDownIcon as HandThumbDownSolid,
} from '@heroicons/react/24/solid';

import { SourceItem } from './SourceTypes';
import type { NarrationEvent } from './NarrationTypes';
import type { AnswerDepth, ResearchDepth } from './AnswerDepth';
import AnswerDepthSelector from './AnswerDepthSelector';

import SourceList from './SourceList';
import { FeedbackModal } from './FeedbackModal';

export interface ChatMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  isLoading?: boolean;
  loadingMessage?: string;
  sources?: SourceItem[];
  // Legacy: the concatenated narration string. Nothing writes or reads it any
  // more — ``narrationEvents`` replaced it, and ``foldNarration`` handles an
  // unlabelled frame too, so there was no case left where the string was the
  // only representation. Declared so previously-persisted messages still type,
  // and dropped from guest storage. Remove once no stored message carries it.
  narrationText?: string;
  // Narration as a structured list, so tool steps and Claude's reasoning can
  // render differently. Not cleared when the answer lands —
  // ``ReasoningDisclosure`` keeps it behind a collapsed control so the reader
  // can go back to the reasoning.
  narrationEvents?: NarrationEvent[];
  // What the backend reports about depth on an assistant message: the depth it
  // ran under, and which research agent actually answered. Both come from the
  // stream rather than from local state, so the label reports what happened
  // rather than what the client asked for.
  answerDepth?: AnswerDepth;
  researchDepth?: ResearchDepth;
  // Set to true when the backend emits a ``memory_updated`` SSE event
  // for this turn — i.e. at least one new durable memory was extracted
  // and persisted from the user's message. Drives the "Memory updated"
  // pill below the assistant response.
  memoryUpdated?: boolean;
}

interface MessageActionsProps {
  onFeedbackSubmit?: (feedbackType: 'thumbs_up' | 'thumbs_down', feedback: string) => void;
  onCopy?: () => void;
  messageText: string;
  showConfirmation?: 'thumbs_up' | 'thumbs_down';
  sources?: SourceItem[];
  /**
   * Whether the body's chips print the numbers the Sources rows carry. False
   * on a turn answered without citations, where the rows are provenance and a
   * number would point at a chip that was never rendered. Passed down from the
   * renderer that already resolved the markers, so the panel cannot disagree
   * with the message above it.
   */
  sourcesAreNumbered?: boolean;
  /**
   * How many of `sources` a chip in the body points at. Those lead the array,
   * so rows past this index are the turn's remaining provenance and render
   * without a number. Defaults to every source, for a caller whose list is
   * cited-only.
   */
  citedCount?: number;
  conversationId?: string | null;
  messageId?: string;
  userId?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  onFeedbackSubmit,
  onCopy,
  messageText,
  showConfirmation,
  sources = [],
  sourcesAreNumbered = true,
  citedCount,
  conversationId,
  messageId,
  userId,
}) => {
  const { t } = useTranslation('chat');
  // Create a unique key for localStorage based on conversation and message
  const storageKey = `sources-expanded-${conversationId}-${messageId}`;

  // Initialize state from localStorage if available
  const [sourcesExpanded, setSourcesExpanded] = React.useState(() => {
    if (typeof window !== 'undefined' && conversationId && messageId) {
      const saved = localStorage.getItem(storageKey);
      return saved === 'true';
    }
    return false;
  });

  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedFeedbackType, setSelectedFeedbackType] = React.useState<
    'thumbs_up' | 'thumbs_down'
  >('thumbs_up');

  // BotMessage already passes sanitized + cited sources; treat as final.
  const safeSources = sources ?? [];

  const handleCopy = () => {
    const lines = messageText.split('\n');
    const plainLines: string[] = [];
    const htmlParts: string[] = [];
    let inTable = false;

    for (const line of lines) {
      const stripped = line.trim();

      // Skip table separator rows
      if (/^\|[\s-:|]+\|$/.test(stripped)) continue;

      // Table rows
      if (/^\|/.test(stripped) && /\|$/.test(stripped)) {
        const cells = stripped
          .replace(/^\|/, '')
          .replace(/\|$/, '')
          .split('|')
          .map((c) =>
            c
              .trim()
              .replace(/\*{1,2}/g, '')
              .replace(/\[(\d+)\]/g, '')
          );

        if (!inTable) {
          inTable = true;
          htmlParts.push(
            '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse">'
          );
          // First row is header
          htmlParts.push('<tr>' + cells.map((c) => `<th>${c}</th>`).join('') + '</tr>');
        } else {
          htmlParts.push('<tr>' + cells.map((c) => `<td>${c}</td>`).join('') + '</tr>');
        }
        plainLines.push(cells.join('\t'));
        continue;
      }

      // Close table if we were in one
      if (inTable) {
        inTable = false;
        htmlParts.push('</table>');
      }

      // Clean line for plain text
      const cleanLine = line
        .replace(/\*{1,2}/g, '')
        .replace(/#{1,6}\s*/g, '')
        .replace(/`(.+?)`/g, '$1')
        .replace(/\[(\d+)\]/g, '');

      plainLines.push(cleanLine);

      // Build HTML for non-table lines
      const htmlLine = line
        .replace(/\[(\d+)\]/g, '')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>');

      if (/^#{1,6}\s/.test(stripped)) {
        const level = stripped.match(/^(#{1,6})/)?.[1].length || 2;
        const text = htmlLine.replace(/^#{1,6}\s+/, '');
        htmlParts.push(`<h${level}>${text}</h${level}>`);
      } else if (/^\s*[-*]\s+/.test(line)) {
        htmlParts.push(`<li>${htmlLine.replace(/^\s*[-*]\s+/, '')}</li>`);
      } else if (stripped) {
        htmlParts.push(`<p>${htmlLine}</p>`);
      }
    }

    if (inTable) htmlParts.push('</table>');

    const plain = plainLines
      .join('\n')
      .replace(/^\s*[-]\s+/gm, '- ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const html = htmlParts.join('\n');

    // Write both rich text and plain text to clipboard
    const blob = new Blob([html], { type: 'text/html' });
    const plainBlob = new Blob([plain], { type: 'text/plain' });
    navigator.clipboard
      .write([new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })])
      .then(() => {
        if (onCopy) onCopy();
      })
      .catch(() => {
        // Clipboard write failed (permission denied or page not focused); do not signal success
      });
  };

  const handleToggleSources = () => {
    const newExpanded = !sourcesExpanded;
    setSourcesExpanded(newExpanded);

    // Persist to localStorage if we have the necessary keys
    if (typeof window !== 'undefined' && conversationId && messageId) {
      localStorage.setItem(storageKey, String(newExpanded));
    }
  };

  const handleFeedbackClick = (feedbackType: 'thumbs_up' | 'thumbs_down') => {
    setSelectedFeedbackType(feedbackType);
    setModalOpen(true);
  };

  const handleFeedbackSubmit = (feedback: string) => {
    if (onFeedbackSubmit) {
      onFeedbackSubmit(selectedFeedbackType, feedback);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  if (!conversationId) return null;

  return (
    <Box>
      <Box display="flex" flexWrap="nowrap" gap={0} mt={2} alignItems="center">
        {/* Thumbs up */}
        {(!showConfirmation || showConfirmation === 'thumbs_up') && (
          <IconButton
            size="small"
            title={t('messageActions.positiveFeedback')}
            aria-label={t('messageActions.positiveFeedback')}
            aria-disabled={showConfirmation ? 'true' : undefined}
            tabIndex={showConfirmation ? -1 : 0}
            onClick={showConfirmation ? undefined : () => handleFeedbackClick('thumbs_up')}
            sx={{
              color: semanticColors.textCaption,
              cursor: showConfirmation ? 'default' : 'pointer',
              '&:hover': { color: semanticColors.textPrimary },
            }}
          >
            {showConfirmation === 'thumbs_up' ? (
              <HandThumbUpSolid width={18} height={18} />
            ) : (
              <HandThumbUpOutline width={18} height={18} />
            )}
          </IconButton>
        )}
        {/* Thumbs down */}
        {(!showConfirmation || showConfirmation === 'thumbs_down') && (
          <IconButton
            size="small"
            title={t('messageActions.negativeFeedback')}
            aria-label={t('messageActions.negativeFeedback')}
            aria-disabled={showConfirmation ? 'true' : undefined}
            tabIndex={showConfirmation ? -1 : 0}
            onClick={showConfirmation ? undefined : () => handleFeedbackClick('thumbs_down')}
            sx={{
              color: semanticColors.textCaption,
              cursor: showConfirmation ? 'default' : 'pointer',
              '&:hover': { color: semanticColors.textPrimary },
            }}
          >
            {showConfirmation === 'thumbs_down' ? (
              <HandThumbDownSolid width={18} height={18} />
            ) : (
              <HandThumbDownOutline width={18} height={18} />
            )}
          </IconButton>
        )}
        {/* Copy */}
        <IconButton
          size="small"
          title={t('messageActions.copyMessage')}
          aria-label={t('messageActions.copyMessage')}
          onClick={handleCopy}
          sx={{
            color: semanticColors.textCaption,
            '&:hover': { color: semanticColors.textPrimary },
          }}
        >
          <ClipboardDocumentIcon width={18} height={18} />
        </IconButton>

        {/* Sources toggle */}
        {safeSources.length > 0 && (
          <Button
            size="small"
            title={t('messageActions.viewSources')}
            aria-expanded={sourcesExpanded}
            aria-controls={`sources-panel-${messageId}`}
            onClick={handleToggleSources}
            sx={{
              color: semanticColors.textCaption,
              textTransform: 'none',
              fontSize: 12,
              minWidth: 'auto',
              ml: 1,
              px: 1.25,
              py: 0.25,
              // Outlined chip: standard border + radius token, no fill.
              border: `1px solid ${primitiveColors.rockSlate}33`,
              borderRadius: BORDER_RADIUS,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
                borderColor: `${primitiveColors.rockSlate}66`,
                color: semanticColors.textPrimary,
              },
            }}
          >
            {t('messageActions.sources', { count: safeSources.length })}
          </Button>
        )}
      </Box>

      {/* Sources panel */}
      {safeSources.length > 0 && (
        <Collapse
          id={`sources-panel-${messageId}`}
          in={sourcesExpanded}
          timeout="auto"
          unmountOnExit
        >
          <Box mt={2}>
            <SourceList
              sources={safeSources}
              numbered={sourcesAreNumbered}
              citedCount={citedCount}
            />
          </Box>
        </Collapse>
      )}

      <Box role="status" aria-live="polite" aria-atomic="true">
        {showConfirmation && (
          <Typography
            variant="caption"
            sx={{
              color: primitiveColors.rockSlate,
              fontSize: 12,
              mt: 0.5,
              display: 'block',
              opacity: 0.8,
            }}
          >
            {t('messageActions.feedbackConfirmed')}
          </Typography>
        )}
      </Box>

      <FeedbackModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleFeedbackSubmit}
        feedbackType={selectedFeedbackType}
        conversationId={conversationId}
        messageId={messageId}
        userId={userId}
      />
    </Box>
  );
};

interface ChatSuggestionProps {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  loading?: boolean;
}

const LOADING_OPACITY_HEX = '80'; // 40% opacity

export const ChatSuggestions: React.FC<ChatSuggestionProps> = ({
  suggestions,
  onSuggestionClick,
  loading,
}) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    justifyContent="flex-end"
    useFlexGap
    sx={{
      background: 'transparent',
      '&.MuiStack-root': {
        gap: { xs: 1, sm: 2 },
      },
    }}
  >
    {suggestions.map((s, i) => (
      <OutlinedButton
        key={i}
        onClick={() => onSuggestionClick(s)}
        gradientOutline={true}
        disabled={loading}
        styleOverrides={{
          root: {
            borderRadius: BORDER_RADIUS,
            background: loading
              ? getBrandGradientWithOpacity(LOADING_OPACITY_HEX)
              : semanticGradients.primary,
            padding: '1px',
          },
          outlinedButton: {
            background: semanticColors.chatBotMessage,
            color: `${primitiveColors.rockSlate}${loading ? LOADING_OPACITY_HEX : ''}`,
            border: '0.5px solid transparent',
            borderRadius: BORDER_RADIUS,
            fontSize: 14,
            padding: '6px 16px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            '&:hover': {
              background: semanticColors.chatBotMessageHover,
              color: `${primitiveColors.rockSlate}${loading ? LOADING_OPACITY_HEX : ''}`,
            },
            '&.Mui-disabled': {
              color: `${primitiveColors.rockSlate}${loading ? LOADING_OPACITY_HEX : ''}`,
            },
          },
        }}
      >
        {s}
      </OutlinedButton>
    ))}
  </Stack>
);

interface ChatInputProps {
  onSend: (value: string) => void | Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  showHelper?: boolean;
  /**
   * Answer depth for the next message, and its setter. Owned by
   * ``ChatContainer`` so the choice survives re-renders of the input and
   * applies to every message in the session, not just the next one.
   */
  depth?: AnswerDepth;
  onDepthChange?: (depth: AnswerDepth) => void;
  /**
   * When true, the composer's top corners render flush (no border-radius)
   * so it docks cleanly under whatever sits directly on top — specifically
   * the ``BallotGrabber`` / ``BallotSheet``'s teal header in the active-chat
   * layout. Default ``false``: the composer keeps all four rounded corners
   * (landing state, no sheet above).
   */
  flushTop?: boolean;
}

const MAX_MESSAGE_LENGTH = 500;

// Soft, downward-biased shadow so the composer reads as a card floating above
// the page. The negative spread keeps the blur from bleeding upward onto the
// teal grabber that docks on top of the composer in the active-chat layout.
const COMPOSER_SHADOW = `0 1px 2px ${semanticColors.shadowLight}, 0 10px 28px -8px rgba(12, 47, 55, 0.22)`;

export const ChatInput = React.memo(function ChatInput({
  onSend,
  disabled,
  placeholder,
  showHelper = true,
  flushTop = false,
  depth,
  onDepthChange,
}: ChatInputProps) {
  const { t } = useTranslation('chat');
  const effectivePlaceholder = placeholder ?? t('input.placeholder');
  const [value, setValue] = useState('');
  const characterCount = value.length;
  // ``maxLength`` already stops typing at the limit; the flag only colors the
  // counter so a voter sees why their text stopped growing.
  const atLimit = characterCount >= MAX_MESSAGE_LENGTH;
  const showCount = characterCount >= 400;
  const canSend = value.trim().length > 0 && !disabled;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(e.target.value);
    },
    []
  );

  const handleSend = useCallback(async () => {
    if (!value.trim() || disabled) return;
    const message = value;
    setValue('');
    try {
      await onSend(message);
    } catch (error) {
      console.error('Failed to send chat message:', error);
    }
  }, [value, disabled, onSend]);

  return (
    <Box
      sx={{
        background: primitiveColors.blue1,
        borderTopLeftRadius: flushTop ? 0 : BORDER_RADIUS_LG,
        borderTopRightRadius: flushTop ? 0 : BORDER_RADIUS_LG,
      }}
    >
      {/* Composer card, two rows: the text on top with the full width to
          itself, the controls on their own row beneath it. A one-line message
          gives a ~94px card, so the 16px text reads at ease instead of being
          squeezed between the buttons. The brand ring appears only while
          something inside has focus. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pt: '6px',
          pb: '8px',
          pl: '16px',
          pr: '8px',
          boxSizing: 'border-box',
          backgroundColor: semanticColors.surface,
          borderRadius: flushTop ? `0 0 ${BORDER_RADIUS_LG} ${BORDER_RADIUS_LG}` : BORDER_RADIUS_LG,
          border: `1px solid ${semanticColors.composerBorder}`,
          boxShadow: COMPOSER_SHADOW,
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:focus-within': {
            borderColor: semanticColors.brandPrimary,
            boxShadow: `${COMPOSER_SHADOW}, 0 0 0 3px ${semanticColors.brandPrimary}26`,
          },
        }}
      >
        <InputBase
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          placeholder={effectivePlaceholder}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          inputProps={{
            maxLength: MAX_MESSAGE_LENGTH,
            'data-testid': 'chat-input',
            'aria-label': t('input.ariaLabel', { maxLength: MAX_MESSAGE_LENGTH }),
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{
            width: '100%',
            py: '10px',
            pr: '8px',
            // 16px is iOS Safari's "no-auto-zoom" threshold — any input
            // smaller than that triggers a viewport zoom when it receives
            // focus.
            fontSize: 16,
            lineHeight: 1.5,
            color: semanticColors.textPrimary,
            '& textarea': { py: 0 },
            '& textarea::placeholder': {
              color: semanticColors.textPlaceholder,
              opacity: 1,
            },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
          {depth && onDepthChange && (
            <AnswerDepthSelector depth={depth} onChange={onDepthChange} disabled={disabled} />
          )}
          {/* Filled circle rather than a bare arrow: the send action is the
              composer's one primary control, so it carries the same dark fill
              as the app's other primary buttons. */}
          <IconButton
            onClick={handleSend}
            disabled={!canSend}
            aria-label={t('input.sendMessage')}
            data-testid="send-button"
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              color: semanticColors.textOnPrimary,
              backgroundColor: semanticColors.textPrimary,
              transition: 'background-color 0.2s ease, transform 0.1s ease',
              '&:hover': { backgroundColor: semanticColors.brandAccent },
              '&:active': { transform: 'scale(0.96)' },
              '&.Mui-disabled': {
                backgroundColor: semanticColors.disabledBackground,
                color: semanticColors.textPlaceholder,
              },
              '&:focus-visible': {
                outline: `2px solid ${semanticColors.brandPrimary}`,
                outlineOffset: '2px',
              },
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
      {/* Footer row: AI disclaimer centered, character count pinned left. The
          count lives here rather than inside the card so it never sits under
          the text it is counting. */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: showHelper ? 18 : 0,
          mt: showHelper ? '6px' : 0,
          px: '4px',
        }}
      >
        {showHelper && (
          <Typography
            variant="caption"
            sx={{
              fontSize: 12,
              lineHeight: 1.4,
              // textTertiary, not textCaption: this row sits on the blue1 page
              // background, where caption grey falls just under 4.5:1.
              color: semanticColors.textTertiary,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {t('input.helperText')}
          </Typography>
        )}
        <Typography
          variant="caption"
          // Live from the start and never hidden from the tree, so the region
          // already exists when the count first appears at 400 characters and
          // that first change is announced. Below the threshold it is empty
          // rather than aria-hidden: an empty live region says nothing, while
          // a hidden one is removed and its first appearance can go unread.
          aria-live="polite"
          sx={{
            position: 'absolute',
            top: showHelper ? 'auto' : '4px',
            left: '12px',
            fontSize: 11,
            lineHeight: 1.4,
            color: atLimit ? 'error.main' : semanticColors.textTertiary,
            opacity: showCount ? 1 : 0,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
          }}
        >
          {showCount ? `${characterCount}/${MAX_MESSAGE_LENGTH}` : ''}
        </Typography>
      </Box>
    </Box>
  );
});
